<div class="compliance-monitoring-program-accordion border-0 rounded-lg" v-for="program in internalPrograms" :key="program.id">
  <!-- Program Header -->
  <div class="compliance-monitoring-program-header" @click.stop="toggleProgram(program.id)">
    <div class="d-flex align-items-center">
      <i class="bi" :class="program.isOpen ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
      <div class="compliance-monitoring-icon-bg-primary ms-2">
        <i class="bi bi-folder-fill"></i>
      </div>
      <div class="ms-3">
        <p class="mb-0 text-xs fw-bold">{{ program.title }}</p>
        <p class="mb-0 text-xxs text-muted">{{ program.projects.length }} requirements</p>
      </div>
    </div>
    <div class="program-meta">
      <span class="text-xxs text-muted">{{ program.createdDate }}</span>
    </div>
  </div>

  <!-- Projects (Questions) -->
  <div v-if="program.isOpen" class="compliance-monitoring-projects-wrapper">
    <div class="compliance-monitoring-project-accordion border table-border-color rounded-lg"
         v-for="(project, index) in program.projects"
         :key="project.id"
         :class="{ 'last-project': index === program.projects.length - 1 }">
      <div class="compliance-monitoring-project-header" @click="toggle_project(project)">
        <div class="compliance-monitoring-project-connector"></div>
        <div class="d-flex align-items-center">
          <i class="bi" :class="openProjectId === project.id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
          <div class="compliance-monitoring-icon-bg-jade ms-2">
            <i class="bi bi-file-earmark"></i>
          </div>
          <div class="ms-3">
            <p class="mb-0 text-xs">{{ project.title }}</p>
          </div>
        </div>
        <div class="project-meta d-flex align-items-center">
          <span class="status-badge" :class="get_status_badge_class(projectStatuses[project.id])">
            {{ projectStatuses[project.id] }}
          </span>
          <div class="compliance-monitoring-dropdown ms-2">
            <button class="btn btn-sm text-dark p-0 border-0" @click.stop="toggleMenu(project.id, $event)">
              <i class="bi bi-three-dots-vertical"></i>
            </button>
            <div class="dropdown-menu shadow-sm w-auto" :class="{ 'show': openMenuId === project.id }">
              <button class="dropdown-item text-xs" data-bs-toggle="modal" :data-bs-target="'#compliance_overview_modal_' + project.id">
                View
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Project Details (Question Expansion) -->
      <div v-if="openProjectId === project.id" class="compliance-monitoring-project-details">
        <!-- Answers Section -->
        <div class="px-3 py-3">
          <div v-if="project.loadingAnswers" class="text-center py-2">
            <div class="spinner-border spinner-border-sm text-primary me-2"></div>
            <span class="text-xxs">Loading submissions...</span>
          </div>

          <div v-else>
            <div v-if="!project.answers || project.answers.length === 0" class="text-center py-2">
              <p class="text-xxs text-muted">No submissions found</p>
            </div>

            <div v-else>
              <div v-for="(answer, a_index) in project.answers" :key="`answer-${a_index}`"
                   class="answer-card p-3 mb-3 rounded border bg-white">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="mb-1 text-xs fw-bold">
                      <i class="fas fa-file-pdf text-danger me-1"></i>
                      {{ extract_file_name(answer.answer) }}
                    </p>
                    <p class="text-xxs text-muted mb-0">
                      Uploaded: {{ format_date(answer.date_answered) }}
                    </p>
                  </div>
                </div>
              </div>

              <div v-if="project.answersPagination?.has_more" class="text-center mt-3">
                <button
                  class="btn btn-sm btn-outline-primary"
                  @click="fetchMoreAnswers(project)"
                  :disabled="project.loadingAnswers"
                >
                  View More Submissions
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Project Modals -->
 <teleport to="body">
<div v-for="project in allProjects" :key="'modal-'+project.id">
  <teleport to="body">
  <div class="modal fade" :id="'compliance_overview_modal_' + project.id" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <p class="modal-title text-white text-xxs">
            Compliance Details
          </p>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body p-3">
          <div class="compliance-overview container-md">
            <div class="list-group">
              <div class="list-group-item border p-4 shadow-sm rounded mb-3 pe-auto">
                <span class="status-badge position-absolute text-xxs top-0 end-0 m-2"
                      :class="get_status_badge_class(projectStatuses[project.id])">
                  {{ projectStatuses[project.id] }}
                </span>

                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="mb-1 text-xs fw-bold">{{ project.title }}</p>
                    <p class="text-xxs text-muted mb-0">
                      {{ project.section }} • {{ project.form }}
                    </p>
                  </div>
                </div>

                <div class="mt-3 pt-3 border-top">
                  <div class="monitoring-rules mb-3">
                    <p class="fw-bold border-bottom pb-2 mb-3 text-xs">Monitoring Rules</p>
                    <div v-if="project.rules?.length" class="rule-card p-3 mb-3 rounded border shadow-sm bg-white">
                      <div v-for="(rule, ruleIndex) in project.rules" :key="ruleIndex">
                        <span class="text-xs fw-bold">Rule:</span>
                        <span class="text-xs">{{ rule.name || 'Unnamed Rule' }}</span>
                        <p v-if="rule.description" class="text-xxs mt-2 mb-1">
                          <strong>Description:</strong> {{ rule.description }}
                        </p>
                        <div class="d-flex gap-2 flex-wrap mt-2">
                          <span class="badge bg-warning text-dark text-xxs">
                            Frequency: {{ format_frequency(rule.frequency) }}
                          </span>
                          <span class="badge bg-light text-dark border text-xxs">
                            Version: {{ rule.version || 'N/A' }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-2">
                      <p class="text-xxs text-muted mb-0">No monitoring rules defined</p>
                    </div>
                  </div>

                  <!-- Answers List in Modal -->
                  <div class="answers-modal-list mt-4">
                    <p class="fw-bold border-bottom pb-2 mb-3 text-xs">Submissions</p>
                    <div v-if="project.loadingAnswers" class="text-center py-2">
                      <div class="spinner-border spinner-border-sm text-primary me-2"></div>
                      <span class="text-xxs">Loading submissions...</span>
                    </div>
                    <div v-else>
                      <div v-if="!project.answers || project.answers.length === 0" class="text-center py-2">
                        <p class="text-xxs text-muted">No submissions found</p>
                      </div>
                      <div v-else>
                        <div
                          v-for="(answer, a_index) in project.answers"
                          :key="`modal-answer-${a_index}`"
                          class="answer-card p-3 mb-3 rounded border bg-white cursor-pointer"
                          @click="toggleModalAnswerHistory(answer)"
                        >
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <p class="mb-1 text-xs fw-bold">
                                <i class="fas fa-file-pdf text-danger me-1"></i>
                                {{ extract_file_name(answer.answer) }}
                              </p>
                              <p class="text-xxs text-muted mb-0">
                                Uploaded: {{ format_date(answer.date_answered) }}
                              </p>
                            </div>
                            <i class="fas" :class="answer.showHistory ? 'fa-angle-up' : 'fa-angle-down'"></i>
                          </div>
                          <transition name="fade">
                            <div v-if="answer.showHistory" class="mt-3">
                              <div v-if="answer.loadingChecks" class="text-center py-2">
                                <div class="spinner-border spinner-border-sm text-secondary me-2"></div>
                                <span class="text-xxs">Loading checks...</span>
                              </div>
                              <div v-else>
                                <div v-if="!answer.checks || answer.checks.length === 0" class="text-center py-2">
                                  <p class="text-xxs text-muted">No compliance checks available</p>
                                </div>
                                <div v-else>
                                  <p class="fw-bold border-bottom pb-2 mb-3 text-xs">Compliance Checks</p>
                                  <div
                                    v-for="(check, c_index) in answer.checks"
                                    :key="`modal-check-${c_index}`"
                                    class="compliance-event mb-3 p-3 border rounded bg-white shadow-sm"
                                    :class="get_history_status_class(check)"
                                  >
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                      <div class="d-flex align-items-center gap-2">
                                        <span
                                          class="status-badge text-xxs fw-bold px-2 py-1 rounded"
                                          :class="get_history_badge_class(check.status)"
                                        >
                                          {{ get_history_status_label(check) }}
                                        </span>
                                        <span class="text-xxs badge bg-light text-dark border">
                                          <i class="fas fa-code-branch me-1"></i>
                                          Rule v{{ check.compliance_rule?.version || 'N/A' }}
                                        </span>
                                      </div>
                                      <span class="text-xxs text-muted">
                                        Check #{{ check.id || 'N/A' }}
                                      </span>
                                    </div>
                                    <div v-if="check.compliance_rule" class="rule-info mb-2 p-2 bg-light rounded">
                                      <p class="text-xxs mb-1">
                                        <i class="fas fa-cog me-1"></i>
                                        <strong>Rule:</strong> {{ check.compliance_rule.name }}
                                      </p>
                                      <p class="text-xxs mb-0 text-muted">
                                        {{ check.compliance_rule.description }}
                                      </p>
                                    </div>
                                    <div class="check-details">
                                      <p class="text-xxs mb-1">
                                        <i class="fas fa-calendar-alt me-1"></i>
                                        <strong>Checked:</strong> {{ format_date(check.checked_at) }}
                                      </p>
                                      <p class="text-xxs mb-1">
                                        <i class="fas fa-clock me-1"></i>
                                        <strong>Next Check:</strong> {{ format_date(check.next_check_date) }}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </transition>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- End Answers List in Modal -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </teleport>
</div>
</teleport>